import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { authFetch } from '../auth/AuthContext';

/*
  TermWorksheet

  The two-layer cash-to-close worksheet for a lender's term sheet.

  Layer 1 (transcription) is the AI's verbatim line items (ex.lineItems): every
  printed labeled figure, tagged to a section, in the order it appears. We do
  not re-derive or re-sum anything here.

  Layer 2 (the worksheet) tags each transcribed line as one of:
    pays      borrower pays from their own funds (feeds cash to close)
    financed  paid from the loan (payoffs, funding tranches) - not out of pocket
    proceeds  net cash the borrower receives (shown separately, never summed in)
    exclude   reference figures (rate, price, ARV, dates) - touch no total

  Cash to close = the live sum of the "pays" lines. Proceeds = the live sum of
  the "proceeds" lines. Both update as you tag. Nothing persists until Save.

  Each line opens PRE-TAGGED from the AI's own classification (its section plus
  the out-of-pocket reasoning it already did), marked with an amber "AI" dot
  until you confirm it. Touching any tag confirms that line (blue "Set").

  Accuracy is checked against the lender's original, shown in the left pane and
  served through the portal Drive link, the same source the Review vs original
  flow uses. Verbiage that is not a line item (conditions, prepay language,
  recourse, anything prose) is carried verbatim in its own block below, never
  tagged and never summed.

  Persistence: the tags are saved under a "worksheet" key INSIDE the existing
  Term summary data JSON blob on the Lender Submission, alongside the keys the
  white-label renderer uses (rows, labels, hidden, oop, extraOop, extraRows).
  We load the whole blob first and merge, so saving the worksheet never wipes a
  saved white-label edit.

  Props:
    submission   the lender submission row (needs id and termSheetDocId)
    lenderName   display name for the header
    onClose      close callback
*/

const TAGS = [
  { key: 'pays',     label: 'Pays',     full: 'Borrower pays', fill: '#154D97', tint: '#E6F1FB', ink: '#0C447C' },
  { key: 'financed', label: 'Financed', full: 'Financed',      fill: '#0F6E56', tint: '#E1F5EE', ink: '#085041' },
  { key: 'proceeds', label: 'Proceeds', full: 'Proceeds',      fill: '#3B6D11', tint: '#EAF3DE', ink: '#27500A' },
  { key: 'exclude',  label: 'Exclude',  full: 'Exclude',       fill: '#5F5E5A', tint: '#F1EFE8', ink: '#444441' },
];
const TAG_BY_KEY = {};
TAGS.forEach((t) => { TAG_BY_KEY[t.key] = t; });

const SECTION_ORDER = [
  ['funding', 'Funding'],
  ['fees', 'Fees'],
  ['pricing', 'Loan pricing'],
  ['leverage', 'Leverage'],
  ['structure', 'Structure'],
  ['dates', 'Dates'],
  ['other', 'Other'],
];

export default function TermWorksheet({ submission, lenderName, onClose }) {
  const docId = submission && submission.termSheetDocId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ex, setEx] = useState(null);
  const [driveLink, setDriveLink] = useState('');
  const [rows, setRows] = useState([]);
  const [savedAll, setSavedAll] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [customSeq, setCustomSeq] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!docId) {
      setError('No term sheet document is linked to this lender yet.');
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const [exRes, ovRes] = await Promise.all([
          authFetch(`/.netlify/functions/get-document-extraction?documentId=${docId}`),
          authFetch(`/.netlify/functions/term-summary?submissionId=${submission.id}`).catch(() => null),
        ]);
        const d = await exRes.json();
        if (!exRes.ok) throw new Error(d.error || 'Failed to load term sheet');
        let blob = null;
        if (ovRes && ovRes.ok) {
          const oj = await ovRes.json().catch(() => null);
          if (oj && oj.data && typeof oj.data === 'object') blob = oj.data;
        }
        if (!alive) return;
        const extracted = d.extracted || null;
        setEx(extracted);
        setDriveLink(d.driveLink || '');
        setSavedAll(blob);
        setRows(buildRows(extracted, blob));
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [docId, submission]);

  const totals = useMemo(() => {
    let ctc = 0;
    let proc = 0;
    let confirmed = 0;
    rows.forEach((r) => {
      if (r.tag === 'pays' && typeof r.amount === 'number') ctc += r.amount;
      if (r.tag === 'proceeds' && typeof r.amount === 'number') proc += r.amount;
      if (r.confirmed) confirmed += 1;
    });
    return { ctc, proc, confirmed, count: rows.length };
  }, [rows]);

  const setTag = useCallback((key, tagKey) => {
    setSaved(false);
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, tag: tagKey, confirmed: true } : r)));
  }, []);

  const confirmAll = useCallback(() => {
    setSaved(false);
    setRows((prev) => prev.map((r) => ({ ...r, confirmed: true })));
  }, []);

  const editCustom = useCallback((key, patch) => {
    setSaved(false);
    setRows((prev) => prev.map((r) => {
      if (r.key !== key) return r;
      const next = { ...r, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'value')) next.amount = parseAmount(patch.value);
      return next;
    }));
  }, []);

  const removeCustom = useCallback((key) => {
    setSaved(false);
    setRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const addLine = useCallback(() => {
    setSaved(false);
    const key = `c${customSeq}`;
    setCustomSeq((n) => n + 1);
    setRows((prev) => prev.concat([{ key, label: '', value: '', section: 'fees', amount: null, tag: 'pays', confirmed: true, custom: true }]));
  }, [customSeq]);

  const save = useCallback(async () => {
    setSaving(true);
    setSaveError('');
    try {
      const tags = {};
      const confirmed = {};
      const extraLines = [];
      rows.forEach((r) => {
        if (r.custom) {
          extraLines.push({ label: r.label, value: r.value, tag: r.tag });
        } else {
          tags[r.key] = r.tag;
          confirmed[r.key] = !!r.confirmed;
        }
      });
      const worksheet = {
        tags,
        confirmed,
        extraLines,
        purpose: ex && ex.outOfPocket ? (ex.outOfPocket.purpose || null) : null,
        cashToClose: Math.round(totals.ctc),
        proceeds: Math.round(totals.proc),
        savedAt: new Date().toISOString(),
      };
      const data = { ...(savedAll || {}), worksheet };
      const res = await authFetch('/.netlify/functions/term-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionId: submission.id, data }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Failed to save');
      setSavedAll(data);
      setSaved(true);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }, [rows, ex, totals, savedAll, submission]);

  const previewUrl = driveLink ? drivePreviewUrl(driveLink) : '';
  const aiOop = ex && ex.outOfPocket ? ex.outOfPocket : null;
  const additional = ex && Array.isArray(ex.additionalTerms) ? ex.additionalTerms.filter(Boolean) : [];
  const conditions = ex && Array.isArray(ex.conditions) ? ex.conditions.filter(Boolean) : [];

  const grouped = useMemo(() => groupRows(rows), [rows]);

  return (
    <div style={w.overlay} onClick={onClose}>
      <div style={w.modal} onClick={(e) => e.stopPropagation()}>
        <div style={w.head}>
          <div>
            <div style={w.title}>{lenderName}</div>
            <div style={w.sub}>Cash to close worksheet</div>
          </div>
          <button style={w.closeBtn} onClick={onClose}><i className="ti ti-x" /></button>
        </div>

        <div style={w.body}>
          {loading && <div style={w.empty}>Loading term sheet...</div>}
          {error && <div style={w.err}>{error}</div>}

          {!loading && !error && (!ex || !Array.isArray(ex.lineItems) || ex.lineItems.length === 0) && (
            <div style={w.note}>
              No transcribed line items to build a worksheet from. The AI could not pull a structured term sheet from this document.
              {driveLink ? <a href={driveLink} target="_blank" rel="noopener noreferrer" style={w.openLink}> Open the original</a> : null}
            </div>
          )}

          {!loading && !error && ex && Array.isArray(ex.lineItems) && ex.lineItems.length > 0 && (
            <div style={w.twoCol}>
              <div style={w.leftCol}>
                <div style={w.colLabel}><i className="ti ti-file-text" style={{ fontSize: '13px', verticalAlign: '-2px', marginRight: '4px' }} /> Original term sheet</div>
                {previewUrl ? (
                  <iframe title="Original term sheet" src={previewUrl} style={w.frame} />
                ) : (
                  <div style={w.noPdf}>
                    Preview not available.
                    {driveLink ? <a href={driveLink} target="_blank" rel="noopener noreferrer" style={w.openLink}> Open original</a> : null}
                  </div>
                )}
              </div>

              <div style={w.rightCol}>
                <div style={w.progressRow}>
                  <span style={w.progressText}>{totals.confirmed} of {totals.count} confirmed</span>
                  <button style={w.confirmAllBtn} onClick={confirmAll}>
                    <i className="ti ti-checks" style={{ fontSize: '13px', verticalAlign: '-2px', marginRight: '4px' }} /> Confirm all as suggested
                  </button>
                </div>

                <div style={w.tiles}>
                  <div style={w.tilePays}>
                    <div style={w.tileK}>Cash to close (out of pocket)</div>
                    <div style={w.tileVPays}>{money(totals.ctc)}</div>
                  </div>
                  <div style={w.tileProc}>
                    <div style={w.tileKProc}>Est. proceeds to borrower</div>
                    <div style={w.tileVProc}>{money(totals.proc)}</div>
                  </div>
                </div>

                {aiOop && (typeof aiOop.cashToClose === 'number' || typeof aiOop.proceeds === 'number') && (
                  <CrossCheck ctc={totals.ctc} aiOop={aiOop} />
                )}

                {grouped.map((g) => (
                  <div key={g.section} style={{ marginTop: '12px' }}>
                    <div style={w.secHead}>{g.title}</div>
                    {g.rows.map((r) => (
                      <WorksheetRow
                        key={r.key}
                        row={r}
                        onTag={(tagKey) => setTag(r.key, tagKey)}
                        onEdit={editCustom}
                        onRemove={removeCustom}
                      />
                    ))}
                  </div>
                ))}

                <button style={w.addBtn} onClick={addLine}>
                  <i className="ti ti-plus" style={{ fontSize: '13px', verticalAlign: '-2px', marginRight: '4px' }} /> Add a line from the original
                </button>

                {(additional.length > 0 || conditions.length > 0) && (
                  <div style={w.verbatimWrap}>
                    <div style={w.secHead}><i className="ti ti-quote" style={{ fontSize: '13px', verticalAlign: '-2px', marginRight: '4px' }} /> Additional terms and conditions</div>
                    {conditions.map((c, i) => (<div key={`c${i}`} style={w.verbatimLine}>{c}</div>))}
                    {additional.map((a, i) => (<div key={`a${i}`} style={w.verbatimLine}>{a}</div>))}
                    <div style={w.verbatimNote}>
                      <i className="ti ti-lock" style={{ fontSize: '12px', verticalAlign: '-2px', marginRight: '4px' }} />
                      Carried verbatim. Never tagged, never summed.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!loading && !error && ex && Array.isArray(ex.lineItems) && ex.lineItems.length > 0 && (
          <div style={w.actions}>
            <div style={w.actionsNote}>
              <i className="ti ti-info-circle" style={{ fontSize: '13px', verticalAlign: '-2px', marginRight: '4px' }} />
              Nothing is saved until you press Save.
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {saveError ? <span style={w.saveErr}>{saveError}</span> : null}
              <button style={w.btnGhost} onClick={onClose} disabled={saving}>
                <i className="ti ti-x" style={{ fontSize: '14px', verticalAlign: '-2px' }} /> Close
              </button>
              <button style={saved ? w.btnSaved : w.btnPrimary} onClick={save} disabled={saving}>
                <i className={`ti ${saving ? 'ti-loader-2' : (saved ? 'ti-check' : 'ti-device-floppy')}`} style={{ fontSize: '14px', verticalAlign: '-2px' }} /> {saving ? 'Saving' : (saved ? 'Saved' : 'Save')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WorksheetRow({ row, onTag, onEdit, onRemove }) {
  const dot = row.confirmed
    ? <span style={w.dotSet}><i className="ti ti-check" style={{ fontSize: '13px' }} /> Set</span>
    : <span style={w.dotAI}><span style={w.dotAmber} /> AI</span>;

  return (
    <div style={w.row}>
      <div style={w.rowLine}>
        <span style={w.rowDot}>{dot}</span>
        {row.custom ? (
          <>
            <input
              style={w.cInput}
              placeholder="Label from the original"
              value={row.label}
              onChange={(e) => onEdit(row.key, { label: e.target.value })}
            />
            <input
              style={w.cValue}
              placeholder="$ amount"
              value={row.value}
              onChange={(e) => onEdit(row.key, { value: e.target.value })}
            />
            <button style={w.rmBtn} onClick={() => onRemove(row.key)} title="Remove line"><i className="ti ti-trash" style={{ fontSize: '13px' }} /></button>
          </>
        ) : (
          <>
            <span style={w.rowLabel}>{row.label}</span>
            <span style={typeof row.amount === 'number' ? w.rowVal : w.rowValMuted}>{row.value}</span>
          </>
        )}
      </div>
      <div style={w.segWrap}>
        {TAGS.map((t, i) => {
          const active = t.key === row.tag;
          const seg = {
            ...w.seg,
            ...(i > 0 ? w.segDiv : null),
            background: active ? t.fill : 'transparent',
            color: active ? '#fff' : '#5F5E5A',
            fontWeight: active ? 600 : 400,
          };
          return (
            <button key={t.key} style={seg} title={t.full} onClick={() => onTag(t.key)}>{t.label}</button>
          );
        })}
      </div>
    </div>
  );
}

function CrossCheck({ ctc, aiOop }) {
  const aiCtc = typeof aiOop.cashToClose === 'number' ? aiOop.cashToClose : null;
  const aiProc = typeof aiOop.proceeds === 'number' ? aiOop.proceeds : null;
  const parts = [];
  if (aiCtc != null) parts.push(`cash to close ${money(aiCtc)}`);
  if (aiProc != null) parts.push(`proceeds ${money(aiProc)}`);
  const ref = `AI estimate: ${parts.join(', ')}.`;

  if (aiCtc == null || aiCtc < 0) {
    return <div style={w.ccNeutral}><i className="ti ti-info-circle" style={{ fontSize: '13px', verticalAlign: '-2px', marginRight: '5px' }} />{ref} The side-by-side original is the real check.</div>;
  }
  const delta = Math.round(ctc) - Math.round(aiCtc);
  if (delta === 0) {
    return <div style={w.ccGood}><i className="ti ti-circle-check" style={{ fontSize: '13px', verticalAlign: '-2px', marginRight: '5px' }} />Your confirmed {money(ctc)} matches the AI estimate.</div>;
  }
  return <div style={w.ccWarn}><i className="ti ti-alert-circle" style={{ fontSize: '13px', verticalAlign: '-2px', marginRight: '5px' }} />Your confirmed {money(ctc)} differs from the AI estimate by {money(Math.abs(delta))}. Check it against the original.</div>;
}

function buildRows(ex, blob) {
  if (!ex || !Array.isArray(ex.lineItems)) return [];
  const saved = blob && blob.worksheet && typeof blob.worksheet === 'object' ? blob.worksheet : null;
  const savedTags = saved && saved.tags ? saved.tags : {};
  const savedConfirmed = saved && saved.confirmed ? saved.confirmed : {};

  const rows = ex.lineItems.map((li, i) => {
    const key = String(i);
    const amount = parseAmount(li && li.value);
    const tag = Object.prototype.hasOwnProperty.call(savedTags, key) ? savedTags[key] : seedTag(li, amount);
    const confirmed = !!savedConfirmed[key];
    return {
      key,
      label: (li && li.label) || '',
      value: (li && li.value) || '',
      section: normalizeSection(li && li.section),
      amount,
      tag,
      confirmed,
      custom: false,
    };
  });

  if (saved && Array.isArray(saved.extraLines)) {
    saved.extraLines.forEach((e, n) => {
      rows.push({
        key: `c${n}`,
        label: (e && e.label) || '',
        value: (e && e.value) || '',
        section: 'fees',
        amount: parseAmount(e && e.value),
        tag: (e && e.tag) || 'pays',
        confirmed: true,
        custom: true,
      });
    });
  }
  return rows;
}

function groupRows(rows) {
  const out = [];
  SECTION_ORDER.forEach(([section, title]) => {
    const inSection = rows.filter((r) => !r.custom && r.section === section);
    if (inSection.length) out.push({ section, title, rows: inSection });
  });
  const custom = rows.filter((r) => r.custom);
  if (custom.length) out.push({ section: 'manual', title: 'Manual additions', rows: custom });
  return out;
}

function normalizeSection(section) {
  const s = String(section || '').toLowerCase();
  const known = ['funding', 'fees', 'pricing', 'leverage', 'structure', 'dates'];
  return known.includes(s) ? s : 'other';
}

function seedTag(li, amount) {
  if (typeof amount !== 'number') return 'exclude';
  const label = String((li && li.label) || '').toLowerCase();
  const section = normalizeSection(li && li.section);
  if (/proceeds|cash to borrower|net to borrower|cash out to borrower/.test(label)) return 'proceeds';
  if (/purchase price|acquisition price|as-is value|as is value|appraised value|after repair|arv|loan amount|max loan|total loan/.test(label)) return 'exclude';
  if (/down payment|borrower (cash|equity|contribution)|cash down|cash from borrower/.test(label)) return 'pays';
  if (/payoff|existing (loan|lien|mortgage|debt)|\blien\b|delinquent tax|taxes (paid|due)|funding|advance|holdback|rehab budget/.test(label)) return 'financed';
  if (section === 'fees') return 'pays';
  if (section === 'funding') return 'financed';
  return 'exclude';
}

function parseAmount(value) {
  if (value == null) return null;
  const str = String(value);
  const dollar = str.indexOf('$');
  if (dollar === -1) return null;
  const after = str.slice(dollar + 1);
  const m = after.match(/-?\d[\d,]*(\.\d+)?/);
  if (!m) return null;
  const n = parseFloat(m[0].replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
}

function money(n) {
  const v = typeof n === 'number' ? n : 0;
  const sign = v < 0 ? '-$' : '$';
  return sign + Math.round(Math.abs(v)).toLocaleString('en-US');
}

function drivePreviewUrl(link) {
  if (!link) return '';
  const a = String(link).match(/\/file\/d\/([^/]+)/);
  if (a) return `https://drive.google.com/file/d/${a[1]}/preview`;
  const b = String(link).match(/[?&]id=([^&]+)/);
  if (b) return `https://drive.google.com/file/d/${b[1]}/preview`;
  return '';
}

const w = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(13,37,69,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', zIndex: 1000, overflowY: 'auto' },
  modal:     { background: '#fff', borderRadius: '12px', width: '96vw', maxWidth: '1080px', boxShadow: '0 8px 40px rgba(13,53,101,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '92vh' },
  head:      { background: '#154D97', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  title:     { color: '#fff', fontSize: '16px', fontWeight: 700 },
  sub:       { color: '#B8D4F0', fontSize: '12px', marginTop: '2px' },
  closeBtn:  { background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '18px', padding: 0 },
  body:      { padding: '16px', overflow: 'auto', flex: 1 },

  twoCol:    { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '16px', alignItems: 'start' },
  leftCol:   { minWidth: 0 },
  rightCol:  { minWidth: 0 },
  colLabel:  { fontSize: '11px', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#3A6BA8', marginBottom: '6px' },
  frame:     { width: '100%', height: '560px', border: '1px solid #C5D5EA', borderRadius: '8px', background: '#F5F9FE' },
  noPdf:     { fontSize: '12.5px', color: '#6a7c98', background: '#F5F9FE', border: '1px solid #EAF1FA', borderRadius: '8px', padding: '16px' },

  progressRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' },
  progressText: { fontSize: '13px', fontWeight: 600, color: '#0D2545' },
  confirmAllBtn:{ display: 'inline-flex', alignItems: 'center', fontSize: '12px', fontWeight: 600, padding: '6px 12px', background: 'transparent', color: '#2B72C2', border: '1.5px solid #B8D4F0', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },

  tiles:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' },
  tilePays:  { background: '#EAF1FA', borderRadius: '8px', padding: '10px 12px' },
  tileProc:  { background: '#EAF3DE', borderRadius: '8px', padding: '10px 12px' },
  tileK:     { fontSize: '11px', color: '#0D3565' },
  tileKProc: { fontSize: '11px', color: '#27500A' },
  tileVPays: { fontSize: '22px', fontWeight: 700, color: '#154D97', marginTop: '2px' },
  tileVProc: { fontSize: '22px', fontWeight: 700, color: '#3B6D11', marginTop: '2px' },

  ccGood:    { fontSize: '12px', color: '#27500A', background: '#EAF3DE', borderRadius: '7px', padding: '8px 11px', marginBottom: '4px' },
  ccWarn:    { fontSize: '12px', color: '#A32D2D', background: '#FCEBEB', borderRadius: '7px', padding: '8px 11px', marginBottom: '4px' },
  ccNeutral: { fontSize: '12px', color: '#444441', background: '#F1EFE8', borderRadius: '7px', padding: '8px 11px', marginBottom: '4px' },

  secHead:   { fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#3A6BA8', margin: '0 0 4px' },
  row:       { padding: '7px 0', borderBottom: '0.5px solid #EAF1FA' },
  rowLine:   { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  rowDot:    { flex: '0 0 auto', width: '48px', fontSize: '11px', fontWeight: 600 },
  dotAI:     { display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#854F0B' },
  dotAmber:  { width: '7px', height: '7px', borderRadius: '50%', background: '#EF9F27', display: 'inline-block' },
  dotSet:    { display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#154D97' },
  rowLabel:  { flex: '1 1 140px', minWidth: '110px', fontSize: '13px', color: '#0D2545' },
  rowVal:    { flex: '0 0 auto', minWidth: '70px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#0D2545' },
  rowValMuted:{ flex: '0 0 auto', minWidth: '70px', textAlign: 'right', fontSize: '13px', color: '#8a9ab5' },

  cInput:    { flex: '1 1 130px', minWidth: '100px', fontSize: '13px', padding: '5px 8px', border: '1px solid #C5D5EA', borderRadius: '6px', fontFamily: 'inherit' },
  cValue:    { flex: '0 0 96px', width: '96px', fontSize: '13px', padding: '5px 8px', border: '1px solid #C5D5EA', borderRadius: '6px', fontFamily: 'inherit', textAlign: 'right' },
  rmBtn:     { flex: '0 0 auto', background: 'transparent', border: 'none', color: '#A23A2E', cursor: 'pointer', padding: '2px 4px' },

  segWrap:   { display: 'inline-flex', border: '0.5px solid #C5D5EA', borderRadius: '7px', overflow: 'hidden', marginTop: '6px' },
  seg:       { fontSize: '11px', padding: '5px 9px', border: 'none', borderRadius: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' },
  segDiv:    { borderLeft: '0.5px solid #EAF1FA' },

  addBtn:    { width: '100%', fontSize: '12px', fontWeight: 600, padding: '8px', marginTop: '10px', background: 'transparent', color: '#2B72C2', border: '1.5px dashed #B8D4F0', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },

  verbatimWrap: { marginTop: '14px', background: '#F0F5FC', border: '0.5px solid #EAF1FA', borderRadius: '8px', padding: '10px 12px' },
  verbatimLine: { fontSize: '12.5px', color: '#0D2545', lineHeight: 1.6, marginBottom: '4px' },
  verbatimNote: { fontSize: '11px', color: '#5F5E5A', marginTop: '6px', borderTop: '0.5px solid #C5D5EA', paddingTop: '6px' },

  actions:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 18px', borderTop: '1px solid #EAF1FA', flexWrap: 'wrap' },
  actionsNote:{ fontSize: '12px', color: '#6a7c98' },
  btnGhost:  { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 600, padding: '8px 16px', background: 'transparent', color: '#154D97', border: '1.5px solid #154D97', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },
  btnPrimary:{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 700, padding: '8px 18px', background: '#154D97', color: '#fff', border: '1.5px solid #154D97', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },
  btnSaved:  { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 700, padding: '8px 18px', background: '#2D5E0E', color: '#fff', border: '1.5px solid #2D5E0E', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit' },
  saveErr:   { fontSize: '12px', color: '#c0392b' },

  openLink:  { display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12.5px', color: '#2B72C2', textDecoration: 'none', fontWeight: 600 },
  empty:     { fontSize: '13px', color: '#8a9ab5', padding: '12px 0' },
  err:       { fontSize: '12px', color: '#c0392b', padding: '6px 0' },
  note:      { fontSize: '12.5px', color: '#6a7c98', background: '#F5F9FE', borderRadius: '6px', padding: '12px' },
};
